import React from 'react';
import { Table, Button, Spinner, Badge } from 'react-bootstrap';
import type { ShippingListProps } from './types';

const ShippingList: React.FC<ShippingListProps> = ({ policies, onEdit, onDelete, isLoading }) => {
  if (isLoading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
        <div className="mt-2">Loading shipping policies...</div>
      </div>
    );
  }

  if (policies.length === 0) {
    return (
      <div className="text-center py-4">
        <div className="text-muted">No shipping policies found.</div>
        <div className="text-muted">Create your first shipping policy to get started.</div>
      </div>
    );
  }

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Table responsive striped hover>
      <thead>
        <tr>
          <th>Country</th>
          <th>Currency</th>
          <th>Shipping Cost</th>
          <th>Free Shipping Threshold</th>
          <th>Delivery Time</th>
          <th>Express Shipping</th>
          <th>Dynamic SLA</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {policies.map((policy) => (
          <tr key={policy.countryCode}>
            <td>
              <div>
                <strong>{policy.countryCode}</strong>
                <br />
                <small className="text-muted">{policy.countryCode}</small>
              </div>
            </td>
            <td>
              <strong>{policy.currencyCode}</strong>
            </td>
            <td>{formatCurrency(policy.shippingCost)}</td>
            <td>
              {policy.freeShippingThreshold ? (
                formatCurrency(policy.freeShippingThreshold)
              ) : (
                <span className="text-muted">No free shipping</span>
              )}
            </td>
            <td>
              {policy.deliveryDuration.minDays === policy.deliveryDuration.maxDays
                ? `${policy.deliveryDuration.minDays} ${policy.deliveryDuration.minDays === 1 ? 'day' : 'days'}`
                : `${policy.deliveryDuration.minDays}-${policy.deliveryDuration.maxDays} days`}
            </td>
            <td>
              {policy.expressShipping ? (
                <div className="text-nowrap">
                  <Badge bg={policy.expressShipping.enabled ? 'primary' : 'secondary'} className="mb-1">
                    {policy.expressShipping.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                  <div className="small">Fee {formatCurrency(policy.expressShipping.shippingFee)}</div>
                  <div className="small">Free from {formatCurrency(policy.expressShipping.freeShippingThreshold)}</div>
                  <div className="small text-muted">
                    {policy.expressShipping.shippingDuration.minDay}-{policy.expressShipping.shippingDuration.maxDay}{' '}
                    business days
                  </div>
                </div>
              ) : (
                <span className="text-muted">Not configured</span>
              )}
            </td>
            <td>
              {policy.isDynamic ? (
                <div>
                  <Badge bg="success" className="mb-1">
                    Dynamic
                  </Badge>
                  {policy.avgDeliveryDay != null && (
                    <>
                      <br />
                      <small>avg {policy.avgDeliveryDay}d</small>
                    </>
                  )}
                </div>
              ) : (
                <Badge bg="secondary">Static</Badge>
              )}
            </td>
            <td>
              <div className="d-flex gap-1">
                <Button variant="outline-primary" size="sm" onClick={() => onEdit(policy)}>
                  Edit
                </Button>
                <Button variant="outline-danger" size="sm" onClick={() => onDelete(policy)}>
                  Delete
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export default ShippingList;
